<template>
  <div
    class="candidate-cursor fixed pointer-events-none"
    :style="{
      visibility: dragging ? null : 'hidden',
      top: posY - deltaY + 'px',
      left: posX - deltaX + 'px'
    }"
    draggable="false"
  >
    <candidate-icon
      :uid="uid"
      :key="'cursor-' + candidateId"
      v-for="candidateId in candidateIds"
      :candidate-id="candidateId"
      :show-name="true"
      :show-match-percent="true"
      :show-position="true"
      :opaque="true"
      :position="getCandidatePosition(candidateId)"
      :small="candidateSize === 'small'"
      class="shadow-lg"
      style="pointer-events: none"
      draggable="false"
    ></candidate-icon>
  </div>
</template>

<script>
import CandidateIcon from "./CandidateIcon";
import Vue from "vue";
import { ViewportHelper } from "@/utils/ViewportHelper";
import { Easings } from "@/utils/Easings";
import { uuid } from "vue-uuid";
import { mapGetters } from "vuex";

/**
 * Listens to dragstart events on the candidateDragBus.
 * Emits a drag event every frame the cursor is being dragged.
 * Emits a dragend event when the drag is released.
 * Emits a drop event if at least one drop interaction is suggested by dragend listeners.
 */
export default {
  name: "CandidateCursor",
  components: { CandidateIcon },
  props: {
    candidateDragBus: {
      type: Vue,
      required: true
    },
    candidateSize: {
      type: String,
      default: "normal"
    }
  },
  data: () => {
    return {
      uid: uuid.v4(),
      candidateIds: [],
      dragging: false,
      isTouchInteraction: false,
      posX: 0,
      posY: 0,
      deltaX: 0,
      deltaY: 0
    };
  },
  mounted() {
    this.candidateDragBus.$on("dragstart", this.onDragStart);
  },
  computed: {
    ...mapGetters("candidate", ["matches"])
  },
  methods: {
    onDragStart(event) {
      if (event.candidateId) {
        this.candidateIds = [];
        this.candidateIds.push(event.candidateId);
      } else {
        this.candidateIds = event.candidateIds;
      }

      this.isTouchInteraction = event.isTouchInteraction;
      this.posX = event.posX;
      this.posY = event.posY;
      this.deltaX = event.deltaX + event.initiationDeltaX;
      this.deltaY = event.deltaY + event.initiationDeltaY;
      this.hookEvents();
      setTimeout(() => {
        this.dragging = true;
        this.startInitiationLerper(
          event.deltaX,
          event.deltaY,
          event.initiationDeltaX,
          event.initiationDeltaY
        );
      }, 10);
      if (this.isTouchInteraction) {
        // noinspection JSUnresolvedVariable
        this.$root.$el.style.pointerEvents = "none";
      }
      const documentSelection = document.getSelection();
      if (documentSelection) {
        documentSelection.empty();
      }
    },
    onDragEnd() {
      // noinspection JSUnresolvedVariable
      this.$root.$el.style.pointerEvents = null;
      this.dragging = false;
      this.releaseHooks();

      const possibleTargets = [];
      this.candidateDragBus.$emit("dragend", {
        candidateId: this.candidateIds[0],
        candidateIds: this.candidateIds,
        clientX: this.posX,
        clientY: this.posY,
        x: this.posX - this.deltaX,
        y: this.posY - this.deltaY,
        targets: possibleTargets
      });
      if (possibleTargets.length > 0) {
        possibleTargets.sort((a, b) => {
          if (a.priority < b.priority) return 1;
          if (a.priority > b.priority) return -1;
          return 0;
        });
        this.candidateDragBus.$emit("drop", {
          candidateId: this.candidateIds[0],
          candidateIds: this.candidateIds,
          clientX: this.posX,
          clientY: this.posY,
          x: this.posX - this.deltaX,
          y: this.posY - this.deltaY,
          target: possibleTargets[0].target
        });
      }
    },
    onPointerMove(event) {
      const position = ViewportHelper.getViewportPosition(event);
      this.posX = position.x;
      this.posY = position.y;
      this.candidateDragBus.$emit("drag", {
        candidateId: this.candidateIds[0],
        candidateIds: this.candidateIds,
        clientX: position.x,
        clientY: position.y,
        x: position.x - this.deltaX,
        y: position.y - this.deltaY
      });
    },
    onPointerUp() {
      this.onDragEnd();
    },
    hookEvents() {
      if (this.isTouchInteraction) {
        document.addEventListener("touchmove", this.onPointerMove);
        document.addEventListener("touchend", this.onPointerUp);
        document.addEventListener("touchcancel", this.onPointerUp);
      } else {
        document.body.addEventListener("mousemove", this.onPointerMove);
        document.body.addEventListener("mouseup", this.onPointerUp);
      }
    },
    releaseHooks() {
      if (this.isTouchInteraction) {
        document.removeEventListener("touchmove", this.onPointerMove);
        document.removeEventListener("touchend", this.onPointerUp);
        document.removeEventListener("touchcancel", this.onPointerUp);
      } else {
        document.body.removeEventListener("mousemove", this.onPointerMove);
        document.body.removeEventListener("mouseup", this.onPointerUp);
      }
    },
    getCandidatePosition(candidateId) {
      const match = this.matches[candidateId];
      return match && match.rank != null ? match.rank : null;
    },
    startInitiationLerper(deltaX, deltaY, initiationDeltaX, initiationDeltaY) {
      const initiationLerpTime = 0.1;
      let t = 0;
      const step = 1 / 50 / initiationLerpTime;
      const initiationLerper = () => {
        if (!this.dragging) {
          return;
        }
        setTimeout(() => {
          t = Math.min(1, t + step);
          const progress = 1 - Easings.easeInOutSine(t);
          this.deltaX = deltaX + progress * initiationDeltaX;
          this.deltaY = deltaY + progress * initiationDeltaY;
          if (t < 1) {
            initiationLerper();
          }
        }, 20);
      };
      initiationLerper();
    }
  }
};
</script>

<style scoped>
.candidate-cursor {
  z-index: 9999;
  touch-action: none;
}
.candidate-cursor .shadow-lg {
  box-shadow: var(--interaction-light) 0 0.3rem 2rem;
}
</style>
